<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { ref, computed, watch, reactive, onMounted, onUnmounted } from "vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { Inertia } from '@inertiajs/inertia';
import DialogModal from '@/Components/DialogModal.vue';
import AddInventoryModal from '@/Components/Inventory/AddInventoryModal.vue'
import SelectInput from '@/Components/SelectInput.vue'
import { useForm, usePage } from '@inertiajs/inertia-vue3';
import { useTenantButtonVisibility, useTenantTextRules } from '@/helpers'

const props = defineProps({
  report: Object
});
const page = usePage();
const { resolveTextRule } = useTenantTextRules();

function openMailPreviewIfPresent() {
  const url = page.props.value?.inventory_mail_preview_url
    ?? page.props.value?.flash?.inventory_mail_preview_url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
const { isButtonEnabled } = useTenantButtonVisibility();

const styleEntityLabel = computed(() =>
  resolveTextRule('inventory.style.entity', 'Style', 'inventory.report.style_entity')
);
const styleNumberLabel = computed(() =>
  resolveTextRule('style_number.label', 'Style Number', 'inventory.report.style_number')
);
const garmentLabel = computed(() =>
  resolveTextRule('garment.label', 'Garment Type', 'inventory.report.garment')
);
const locationLabel = computed(() =>
  resolveTextRule('location.label', 'Location', 'inventory.report.location')
);
const boxLabel = computed(() =>
  resolveTextRule('inventory.filters.box_label', 'Box', 'inventory.report.box')
);
const reportTitle = computed(() =>
  resolveTextRule('inventory.report.title', 'Report View / Edit', 'inventory.report.title')
);
const extraField1Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_1_label', 'Extra Field 1', 'inventory.report.extra_field_1_label')
);
const extraField2Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_2_label', 'Extra Field 2', 'inventory.report.extra_field_2_label')
);
const extraField3Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_3_label', 'Extra Field 3', 'inventory.report.extra_field_3_label')
);
const isExtraField1Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_1', false));
const isExtraField2Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_2', false));
const isExtraField3Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_3', false));
const colorNameLabel = computed(() =>
  resolveTextRule('inventory.style_edit.color_name', 'Color', 'inventory.report.color_name')
);

function valueForEditor(val) {
  if (val === 0 || val === '0' || val == null) return '';
  return val;
}
function normalizeForSubmit(val) {
  if (val === '' || val == null) return 0;
  return val;
}

onMounted(() => {
  try {
    const ref = document.referrer || '';
    const cameFromIndex = /\/inventory(\/|\?|$)/.test(ref) && !/\/inventory\/\d+\/report/.test(ref);
    if (cameFromIndex) {
      sessionStorage.setItem('inventory:index:return', ref);
    }
  } catch { }

  const fromHash = getHashParam('boxNum');
  if (fromHash && /^\d+$/.test(fromHash)) {
    selectByBoxNumber(fromHash);
  }

  openMailPreviewIfPresent();

  window.addEventListener('scroll', hideBoxTooltipNow, true);
  window.addEventListener('resize', hideBoxTooltipNow);
});

onUnmounted(() => {
  window.removeEventListener('scroll', hideBoxTooltipNow, true);
  window.removeEventListener('resize', hideBoxTooltipNow);
  clearTimeout(hideTooltipTimer);
});

function selectByBoxNumber(boxNum) {
  for (const color of Object.keys(props.report.boxOptionsByColor || {})) {
    const boxes = props.report.boxOptionsByColor[color] || [];
    if (boxes.includes(String(boxNum))) {
      selectedColor.value = color;
      requestAnimationFrame(() => { selectedBox.value = String(boxNum); });
      return;
    }
  }
}

console.log('Report Props:', props.report);

const selectedColor = ref(props.report.color);
const selectedBox = ref('All Boxes');

function totalForMainOptAllBoxes(main, opt) {
  const colorQuantities = props.report.quantities?.[selectedColor.value];
  if (!colorQuantities) return 0;

  return Object.keys(colorQuantities).reduce((sum, box) => {
    let val;
    if (selectedBox.value !== 'All Boxes' && box === selectedBox.value) {
      val = editedQuantities[main]?.[opt];
    } else {
      val = colorQuantities[box]?.[main]?.[opt] ?? 0;
    }
    return sum + asNumber(val);
  }, 0);
}

function columnSumGlobal(main) {
  if (props.report.optSizes.length > 0) {
    return props.report.optSizes.reduce(
      (sum, opt) => sum + totalForMainOptAllBoxes(main, opt),
      0
    );
  }
  return totalForMainOptAllBoxes(main, 'Qty');
}

function loadLevelCellStyle(main) {
  const limit = props.report.loadLimitsByMain?.[main] ?? 0;
  if (limit == null || limit === '' || Number(limit) <= 0) {
    return { backgroundColor: '#000000' };
  }
  const sum = columnSumGlobal(main);
  const limitNum = Number(limit);
  const yellowThreshold = limitNum * 1.1;

  if (sum <= limitNum) {
    return { backgroundColor: 'hsl(0, 72%, 74%)' };
  }
  if (sum <= yellowThreshold) {
    return { backgroundColor: 'hsl(48, 90%, 72%)' };
  }
  return { backgroundColor: 'hsl(120, 55%, 72%)' };
}

const selectedImage = computed(() => {
  const index = props.report.colorOptions.indexOf(selectedColor.value);
  return props.report.colorImages?.[index] || null;
});

const row = ref('');
const rack = ref('');
const shelf = ref('');
const conveyorSlot = ref('');

const initialBoxInfo = props.report.boxMetadata?.[selectedColor.value]?.[selectedBox.value] || {};
row.value = initialBoxInfo.row || '';
rack.value = initialBoxInfo.rack || '';
shelf.value = initialBoxInfo.shelf || '';
conveyorSlot.value = initialBoxInfo.conveyorSlot || '';

watch(selectedColor, (newColor) => {
  removeBoxNumFromUrl();

  const boxes = props.report.boxOptionsByColor?.[newColor] ?? [];
  selectedBox.value = boxes.length > 0 ? 'All Boxes' : '';
});

const availableBoxes = computed(() => {
  const boxes = props.report.boxOptionsByColor?.[selectedColor.value] || [];
  return ['All Boxes', ...boxes];
});

const editedQuantities = reactive({});

watch([selectedColor, selectedBox], ([color, box]) => {
  const boxInfo = props.report.boxMetadata?.[color]?.[box] || {};
  row.value = boxInfo.row || '';
  rack.value = boxInfo.rack || '';
  shelf.value = boxInfo.shelf || '';
  conveyorSlot.value = boxInfo.conveyorSlot || '';

  if (box && box !== 'All Boxes') {
    addBoxNumToUrl(String(box));
  } else {
    removeBoxNumFromUrl();
  }

  for (const key in editedQuantities) delete editedQuantities[key];

  if (props.report.optSizes.length > 0) {
    props.report.mainSizes.forEach((main) => {
      editedQuantities[main] = {};
      props.report.optSizes.forEach((opt) => {
        const raw = props.report.quantities?.[color]?.[box]?.[main]?.[opt];
        editedQuantities[main][opt] = valueForEditor(raw);
      });
    });
  } else {
    props.report.mainSizes.forEach((main) => {
      editedQuantities[main] = {};
      const raw = props.report.quantities?.[color]?.[box]?.[main]?.['Qty'];
      editedQuantities[main]['Qty'] = valueForEditor(raw);
    });
  }
});

const showSuccessModal = ref(false);

function updateBoxInfo() {
  const payload = {
    styleId: props.report.styleId,
    color: selectedColor.value,
    box: selectedBox.value,
    row: row.value,
    rack: rack.value,
    shelf: shelf.value,
    conveyor_slot: conveyorSlot.value || null,
  };

  Inertia.post(
    route('inventory.report.update-box-info'),
    payload,
    {
      preserveScroll: true,
      onSuccess: () => {
        const num = getCurrentBoxNum();
        if (num) addBoxNumToUrl(num); else removeBoxNumFromUrl();
      },
      onError: (errors) => {
        console.error('Update failed:', errors);
        alert('Failed to update box information');
      },
    }
  );
}

const showDeleteModal = ref(false);
function confirmDeleteBox() { showDeleteModal.value = true; }
function cancelDeleteBox() { showDeleteModal.value = false; }

function proceedToDeleteBox() {
  showDeleteModal.value = false;

  Inertia.post(
    route('inventory.report.delete-box'),
    {
      styleId: props.report.styleId,
      color: selectedColor.value,
      box: selectedBox.value,
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        selectedBox.value = 'All Boxes';
        removeBoxNumFromUrl();
        showSuccessModal.value = true;
      },
      onError: (error) => {
        console.error('Failed to delete box:', error);
        alert('Error deleting box');
      }
    }
  );
}

function getQuantity(main, opt) {
  const colorQuantities = props.report.quantities?.[selectedColor.value];
  if (!colorQuantities) return '';

  if (selectedBox.value === 'All Boxes') {
    return Object.values(colorQuantities).reduce((sum, boxQty) => {
      const val = boxQty?.[main]?.[opt] || 0;
      return sum + (typeof val === 'number' ? val : parseInt(val) || 0);
    }, 0);
  } else {
    return colorQuantities?.[selectedBox.value]?.[main]?.[opt] || '';
  }
}

const mergeTargetBox = ref('');
function mergeBoxes() {
  Inertia.post(
    route('inventory.report.merge-box'),
    {
      currentBoxId: selectedBox.value,
      targetBoxId: mergeTargetBox.value,
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        selectedBox.value = 'All Boxes';
        removeBoxNumFromUrl();
        mergeTargetBox.value = '';
      },
      onError: (error) => {
        console.error('Merge failed:', error);
        alert('Error merging boxes');
      }
    }
  );
}

const selectedLocation = ref('');
const selectedStorage = ref('');
const storages = ref([]);
const loadingStorage = ref(false);
const showStorageError = ref(false);

const locationConveyorSlot = ref('');

watch(selectedLocation, async (newVal) => {
  selectedStorage.value = '';
  storages.value = [];
  showStorageError.value = false;
  locationConveyorSlot.value = '';

  if (newVal) {
    loadingStorage.value = true;
    try {
      const response = await axios.get(`/api/locations/${newVal}/storages`);
      storages.value = response.data;
      showStorageError.value = storages.value.length === 0;
    } catch (err) {
      showStorageError.value = true;
    } finally {
      loadingStorage.value = false;
    }
  }
});

function updateLocation() {
  const payload = {
    styleId: props.report.styleId,
    color: selectedColor.value,
    box: selectedBox.value,
    locationId: selectedLocation.value,
    storageId: selectedStorage.value
  };

  if (selectedLocationType.value === 'conveyor') {
    if (locationConveyorSlot.value === '') {
      alert('Please enter a conveyor slot.');
      return;
    }
    payload.conveyor_slot = locationConveyorSlot.value || null;
  }

  Inertia.post(
    route('inventory.report.update-box-location'),
    payload,
    {
      preserveScroll: true,
      onSuccess: () => {
        const num = getCurrentBoxNum();
        if (num) addBoxNumToUrl(num); else removeBoxNumFromUrl();
      },
      onError: (error) => {
        console.error('Error updating location:', error);
      }
    }
  );
}

const printSection = ref(null);
function printSectionContent() {
  const printContents = printSection.value.innerHTML;
  const originalContents = document.body.innerHTML;

  document.body.innerHTML = printContents;
  window.print();
  document.body.innerHTML = originalContents;
  window.location.reload();
}

const showAddInventoryModal = ref(false);
function openAddInventoryModal() {
  showAddInventoryModal.value = true;
}

const selectedLocationType = computed(() => {
  return props.report.locations.find(loc => loc.locationId === selectedLocation.value)?.type || '';
});

function goBack() {
  try {
    const saved = sessionStorage.getItem('inventory:index:return');
    if (saved) {
      sessionStorage.removeItem('inventory:index:return');
      window.location.replace(saved);
      return;
    }
  } catch { }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  Inertia.visit(route('inventory.index'), { replace: true });
}

const tooltipLinks = computed(() => props.report.tooltipLinks || {});

//agregue
function getCurrentBoxQuantities(color, box) {
  return props.report.quantities?.[color]?.[box] || {};
}

function asNumber(val) {
  if (val === '' || val == null || Number.isNaN(Number(val))) return 0;
  return Number(val);
}

function updateInventory() {
  /*const toSend = {};
  if (props.report.optSizes.length > 0) {
    props.report.mainSizes.forEach((main) => {
      toSend[main] = {};
      props.report.optSizes.forEach((opt) => {
        toSend[main][opt] = normalizeForSubmit(editedQuantities[main]?.[opt]);
      });
    });
  } else {
    props.report.mainSizes.forEach((main) => {
      toSend[main] = {};
      toSend[main]['Qty'] = normalizeForSubmit(editedQuantities[main]?.['Qty']);
    });
  }*/

  const current = getCurrentBoxQuantities(selectedColor.value, selectedBox.value);

  const changes = {};

  if (props.report.optSizes.length > 0) {
    props.report.mainSizes.forEach((main) => {
      props.report.optSizes.forEach((opt) => {
        const edited = editedQuantities[main]?.[opt];
        const newVal = asNumber(edited);
        const currVal = asNumber(current?.[main]?.[opt]);

        if (newVal !== currVal) {
          if (!changes[main]) changes[main] = {};
          changes[main][opt] = newVal;
        }
      });
    });
  } else {
    props.report.mainSizes.forEach((main) => {
      const edited = editedQuantities[main]?.['Qty'];
      const newVal = asNumber(edited);
      const currVal = asNumber(current?.[main]?.['Qty']);
      if (newVal !== currVal) {
        if (!changes[main]) changes[main] = {};
        changes[main]['Qty'] = newVal;
      }
    });
  }

  if (Object.keys(changes).length === 0) {
    return;
  }

  Inertia.post(
    route('inventory.update-quantities'),
    {
      styleId: props.report.styleId,
      color: selectedColor.value,
      box: selectedBox.value,
      quantities: changes
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        const num = getCurrentBoxNum();
        if (num) addBoxNumToUrl(num); else removeBoxNumFromUrl();
        openMailPreviewIfPresent();
      }
    }
  );
}

function handleInput(main, opt) {
  if (editedQuantities[main][opt] === null || editedQuantities[main][opt] === '') {
    editedQuantities[main][opt] = '';
  }
}

function getCellTooltipLinks(color, main, opt) {
  return tooltipLinks.value[color]?.[main]?.[opt] ?? [];
}

function hasCellTooltipLinks(color, main, opt) {
  return getCellTooltipLinks(color, main, opt).length > 0;
}

const boxTooltip = reactive({
  visible: false,
  top: 0,
  left: 0,
  links: [],
});

let hideTooltipTimer = null;

function showBoxTooltip(event, color, main, opt) {
  if (selectedBox.value !== 'All Boxes') return;
  const links = getCellTooltipLinks(color, main, opt);
  if (links.length === 0) return;

  clearTimeout(hideTooltipTimer);
  const rect = event.currentTarget.getBoundingClientRect();
  boxTooltip.top = rect.bottom + 4;
  boxTooltip.left = rect.left;
  boxTooltip.links = links;
  boxTooltip.visible = true;
}

function scheduleHideBoxTooltip() {
  clearTimeout(hideTooltipTimer);
  hideTooltipTimer = setTimeout(() => {
    boxTooltip.visible = false;
    boxTooltip.links = [];
  }, 150);
}

function keepBoxTooltip() {
  clearTimeout(hideTooltipTimer);
}

function hideBoxTooltipNow() {
  clearTimeout(hideTooltipTimer);
  boxTooltip.visible = false;
  boxTooltip.links = [];
}

function addBoxNumToUrl(boxNum) {
  try { setHashParam('boxNum', String(boxNum)); } catch (e) { console.error('addBoxNumToUrl error', e); }
}
function removeBoxNumFromUrl() {
  try { removeHashParam('boxNum'); } catch (e) { console.error('removeBoxNumFromUrl error', e); }
}
function getCurrentBoxNum() {
  return (selectedBox.value && selectedBox.value !== 'All Boxes') ? String(selectedBox.value) : null;
}
function getHashParam(name) {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  return params.get(name);
}
function setHashParam(name, value) {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  if (value == null || value === '') {
    params.delete(name);
  } else {
    params.set(name, String(value));
  }
  const newHash = params.toString();
  const base = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState({}, '', newHash ? `${base}#${newHash}` : base);
}
function removeHashParam(name) {
  setHashParam(name, null);
}

// Functions for email
const sendingInventoryEmail = ref({ show: false, sending: false, error: null });

const sendInventoryEmailForm = useForm({
  email: '',
  subject: '',
  message: '',
  html: '',
  styleId: props.report.styleId,
  style: props.report.style,
  color: selectedColor.value,
  garment: props.report.garmentType,
  box: selectedBox.value,
});

function openInventoryEmailModal() {
  sendingInventoryEmail.value.error = null;
  sendingInventoryEmail.value.show = true;

  const scope =
    selectedBox.value === 'All Boxes'
      ? '(All Boxes)'
      : `(Box ${selectedBox.value})`;

  const parts = [
    resolveTextRule('mail.inventory.report.title', 'Inventory Report', 'inventory.report.email.subject_title'),
    props.report.style ? `${styleNumberLabel.value} ${props.report.style}` : null,
    selectedColor.value ? `- ${selectedColor.value}` : null
  ].filter(Boolean);

  sendInventoryEmailForm.subject = parts.join(' ');
}

function closeInventoryEmailModal() {
  sendingInventoryEmail.value = { show: false, sending: false, error: null };
  sendInventoryEmailForm.reset();
  sendInventoryEmailForm.styleId = props.report.styleId;
  sendInventoryEmailForm.style = props.report.style;
  sendInventoryEmailForm.color = selectedColor.value;
  sendInventoryEmailForm.garment = props.report.garmentType;
  sendInventoryEmailForm.box = selectedBox.value;
}

function buildEmailHtmlFromPrintSection() {
  if (!printSection.value) return '';

  const table = printSection.value.querySelector('table');
  if (!table) return '';

  const clone = table.cloneNode(true);

  clone.querySelectorAll('input').forEach((inp) => {
    const td = inp.closest('td') || inp.parentElement;
    const span = document.createElement('span');
    const val = inp.value ?? '';
    span.textContent = (val === '' || val == null) ? '' : String(val);
    if (td) td.replaceChild(span, inp);
  });

  clone.querySelectorAll('.absolute, [style*="position:absolute"]').forEach((el) => el.remove());

  clone.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
  clone.removeAttribute('class');

  clone.setAttribute(
    'style',
    [
      'width:100%',
      'border-collapse:collapse',
      'table-layout:auto',
      'font-family:Helvetica,Arial,sans-serif',
      'font-size:12px',
      'color:#111827'
    ].join(';')
  );

  clone.querySelectorAll('thead tr').forEach((tr) => {
    tr.setAttribute('style', 'background:#F9FAFB;');
  });
  clone.querySelectorAll('thead th').forEach((th) => {
    th.setAttribute(
      'style',
      [
        'border:1px solid #E5E7EB',
        'padding:6px',
        'text-align:left',
        'font-weight:600',
        'font-size:12px',
        'background:#F9FAFB'
      ].join(';')
    );
  });
  clone.querySelectorAll('tbody td').forEach((td) => {
    td.setAttribute(
      'style',
      [
        'border:1px solid #E5E7EB',
        'padding:6px',
        'font-size:12px',
        'text-align:left',
        'vertical-align:top',
        'background:#FFFFFF'
      ].join(';')
    );
  });

  const garmentType = (props.report?.garmentType ?? '').toString();
  const color = (selectedColor.value ?? '').toString();
  const imageUrl = (selectedImage.value ?? '').toString();
  const garmentTypeLabel = garmentLabel.value;
  const colorLabel = colorNameLabel.value;

  const header = document.createElement('table');
  header.setAttribute(
    'style',
    [
      'width:100%',
      'border-collapse:collapse',
      'table-layout:auto',
      'font-family:Helvetica,Arial,sans-serif',
      'font-size:14px',
      'color:#111827',
      'margin:0 0 16px 0'
    ].join(';')
  );

  const htr = document.createElement('tr');

  const left = document.createElement('td');
  left.setAttribute('style', 'vertical-align:top; padding:0;');
  const gt = document.createElement('div');
  gt.setAttribute('style', 'margin:0 0 6px 0;');
  gt.innerHTML = `<span style="font-weight:600;">${garmentTypeLabel}:</span> <span>${garmentType || '-'}</span>`;
  const co = document.createElement('div');
  co.setAttribute('style', 'margin:0 0 6px 0;');
  co.innerHTML = `<span style="font-weight:600;">${colorLabel}:</span> <span>${color || '-'}</span>`;
  left.appendChild(gt);
  left.appendChild(co);

  const right = document.createElement('td');
  right.setAttribute('style', 'vertical-align:top; text-align:right; padding:0; width:220px;');
  if (imageUrl) {
    const img = document.createElement('img');
    img.setAttribute('src', imageUrl);
    img.setAttribute(
      'style',
      [
        'max-width:160px',
        'max-height:120px',
        'width:auto',
        'height:auto',
        'border-radius:6px',
        'border:1px solid #E5E7EB',
        'display:inline-block'
      ].join(';')
    );
    img.setAttribute('alt', 'Color image');
    right.appendChild(img);
  }

  htr.appendChild(left);
  htr.appendChild(right);
  header.appendChild(htr);

  const wrapper = document.createElement('div');
  wrapper.setAttribute(
    'style',
    [
      'font-family:Helvetica,Arial,sans-serif',
      'font-size:12px',
      'color:#111827',
      'margin:0',
      'padding:0'
    ].join(';')
  );

  wrapper.appendChild(header);
  wrapper.appendChild(clone);

  return wrapper.outerHTML;
}

function sendInventoryEmail() {
  sendingInventoryEmail.value.error = null;

  if (!sendInventoryEmailForm.email) {
    sendingInventoryEmail.value.error = 'Recipient is required';
    return;
  }
  if (!sendInventoryEmailForm.subject) {
    sendingInventoryEmail.value.error = 'Subject is required';
    return;
  }

  const html = buildEmailHtmlFromPrintSection();
  if (!html) {
    sendingInventoryEmail.value.error = 'Could not capture report content';
    return;
  }

  sendInventoryEmailForm.html = html;
  sendInventoryEmailForm.styleId = props.report.styleId;
  sendInventoryEmailForm.style = props.report.style;
  sendInventoryEmailForm.color = selectedColor.value;
  sendInventoryEmailForm.garment = props.report.garmentType;
  sendInventoryEmailForm.box = selectedBox.value;

  sendingInventoryEmail.value.sending = true;

  sendInventoryEmailForm.post(route('inventory.report.send-email'), {
    preserveScroll: true,
    onSuccess: () => {
      sendingInventoryEmail.value.sending = false;
      closeInventoryEmailModal();
    },
    onError: () => {
      sendingInventoryEmail.value.sending = false;
      sendingInventoryEmail.value.error = 'Email could not be sent';
    },
    onFinish: () => {
      sendingInventoryEmail.value.sending = false;
    }
  });
}

const selectedBoxMetadata = computed(() => {
  return props.report.boxMetadata?.[selectedColor.value]?.[selectedBox.value] || {};
});

const selectedBoxLocationType = computed(() => {
  const locId = selectedBoxMetadata.value.locationId;
  if (!locId) return '';

  const loc = props.report.locations.find((l) => l.locationId === locId);
  return loc?.type || '';
});

</script>

<template>
  <AppLayout :title="reportTitle">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ reportTitle }} ({{ report.style }})
      </h2>
    </template>

    <div class="max-w-7xl mx-auto px-6 mt-4">
      <SecondaryButton @click="goBack">
        ← Back
      </SecondaryButton>
    </div>

    <div class="max-w-7xl mx-auto px-6 py-8">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
        <div>
          <label for="color" class="block text-sm font-medium text-gray-700 mb-1">{{ colorNameLabel }}</label>
          <SelectInput v-model="selectedColor" id="color" class="mt-1"
            :disabled="report.colorOptions.length === 0">
            <option v-if="report.colorOptions.length === 0" disabled value="">----No colors----</option>
            <option v-for="(color, index) in report.colorOptions" :key="index" :value="color">
              {{ color }}
            </option>
          </SelectInput>
        </div>

        <div>
          <label for="box" class="block text-sm font-medium text-gray-700 mb-1">{{ boxLabel }}</label>
          <SelectInput v-model="selectedBox" id="box" class="mt-1"
            :disabled="report.colorOptions.length === 0">
            <option v-if="report.colorOptions.length === 0" disabled value="">----</option>
            <option v-else-if="(report.boxOptionsByColor[selectedColor] || []).length === 0" disabled value="">
              ----No boxes----
            </option>
            <option v-else value="All Boxes">All Boxes</option>
            <option v-for="(box, index) in report.boxOptionsByColor[selectedColor]" :key="index" :value="box">
              {{ box }}
            </option>
          </SelectInput>
        </div>

      </div>

      <div ref="printSection">
        <div class="bg-gray-200 p-6 rounded-md grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div><span class="font-semibold">{{ styleNumberLabel }}:</span> {{ report.style }}</div>
          <div><span class="font-semibold">{{ garmentLabel }}:</span> {{ report.garmentType }}</div>
          <div><span class="font-semibold">Client:</span> {{ report.client }}</div>
          <div v-if="selectedImage" class="row-span-4 mt-4">
            <img :src="selectedImage" alt="Color image" class="w-32 h-auto rounded" />
          </div>
          <div><span class="font-semibold">Gender:</span> {{ report.gender }}</div>
          <div v-if="isExtraField1Visible"><span class="font-semibold">{{ extraField1Label }}:</span> {{ report.extra_field_1 || '-' }}</div>
          <div v-if="isExtraField2Visible"><span class="font-semibold">{{ extraField2Label }}:</span> {{ report.extra_field_2 || '-' }}</div>
          <div v-if="isExtraField3Visible"><span class="font-semibold">{{ extraField3Label }}:</span> {{ report.extra_field_3_labels?.length ? report.extra_field_3_labels.join(', ') : '-' }}</div>

          <div><span class="font-semibold">{{ colorNameLabel }}:</span> {{ selectedColor }}</div>
          <div><span class="font-semibold">Employee:</span> {{ report.employee }}</div>
          <div><span class="font-semibold">Date Edited:</span> {{ report.dateEntered }}</div>
          <div>
            <span class="font-semibold">{{ locationLabel }}: </span>
            <template v-if="selectedBox === 'All Boxes'">
              All Locations
            </template>
            <template v-else>
              <template v-if="selectedBoxMetadata.locationIdentifier">
                <template v-if="selectedBoxLocationType === 'conveyor' && selectedBoxMetadata.conveyorSlot">
                  {{ selectedBoxMetadata.locationIdentifier }}_{{ selectedBoxMetadata.storageName }} - slot: {{
                    selectedBoxMetadata.conveyorSlot }}
                </template>

                <template v-else>
                  {{ selectedBoxMetadata.locationIdentifier }}_{{ selectedBoxMetadata.storageName }}
                </template>
              </template>
              <template v-else>
                -
              </template>
            </template>
          </div>
          <div><span class="font-semibold">{{ boxLabel }}:</span> {{ selectedBox }}</div>
          <div><span class="font-semibold">Row:</span>
            {{ selectedBox === 'All Boxes' ? '-' : report.boxMetadata?.[selectedColor]?.[selectedBox]?.row || '-' }}
          </div>
          <div><span class="font-semibold">Rack:</span>
            {{ selectedBox === 'All Boxes' ? '-' : report.boxMetadata?.[selectedColor]?.[selectedBox]?.rack || '-' }}
          </div>
          <div><span class="font-semibold">Shelf:</span>
            {{ selectedBox === 'All Boxes' ? '-' : report.boxMetadata?.[selectedColor]?.[selectedBox]?.shelf || '-' }}
          </div>

        </div>

        <div>
          <div
            v-if="!report.boxOptionsByColor?.[selectedColor] || report.boxOptionsByColor[selectedColor].length === 0">
            <p class="text-gray-600 italic mt-8">No stock available for this color.</p>
          </div>
          <div class="mt-8 rounded overflow-x-auto">
            <table class="min-w-full text-left text-sm font-light">
              <thead class="border-b font-medium dark:border-neutral-500">
                <tr
                  v-if="report.mainSizes.length > 0"
                  class="border-b border-gray-200"
                >
                  <th class="px-3 py-2 text-left align-middle text-xs font-medium text-gray-600"
                    :class="selectedBox !== 'All Boxes' ? 'sticky left-0 z-20 bg-gray-50' : ''">
                    Load level
                  </th>
                  <th v-for="main in report.mainSizes" :key="'load-' + main"
                    class="px-2 py-2 align-middle min-h-[2.75rem] min-w-[3rem] transition-colors"
                    :style="loadLevelCellStyle(main)"
                    :aria-label="`Load level for size ${main}`"
                  ></th>
                </tr>
                <tr>
                  <th class="px-3 py-2 text-left"
                    :class="selectedBox !== 'All Boxes' ? 'sticky left-0 z-20 bg-gray-50' : ''">#</th>
                  <th v-for="main in report.mainSizes" :key="'header-' + main" class="px-3 py-2 text-left">
                    {{ main }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="report.optSizes.length > 0">
                  <tr v-for="opt in report.optSizes" :key="'row-' + opt"
                    class="border-b border-gray-200 hover:bg-gray-100">
                    <td class="px-3 py-2"
                      :class="selectedBox !== 'All Boxes' ? 'sticky left-0 z-10 bg-white border-r border-gray-200' : ''">
                      {{ opt }}</td>
                    <td v-for="main in report.mainSizes" :key="'cell-' + main + '-' + opt"
                      class="px-3 py-2"
                      :class="{ 'cursor-default': selectedBox === 'All Boxes' && hasCellTooltipLinks(selectedColor, main, opt) }"
                      @mouseenter="showBoxTooltip($event, selectedColor, main, opt)"
                      @mouseleave="scheduleHideBoxTooltip">
                      <span v-if="selectedBox === 'All Boxes'">{{ getQuantity(main, opt) ? getQuantity(main, opt) : ''
                        }}</span>
                      <input v-if="selectedBox !== 'All Boxes'" v-model.number="editedQuantities[main][opt]"
                        type="number"
                        class="border-none outline-none rounded-md shadow-none px-3 py-2 w-full min-w-[80px]"
                        @input="handleInput(main, opt)" />
                    </td>
                  </tr>
                </template>

                <template v-else>
                  <tr class="border-b border-gray-200 hover:bg-gray-100">
                    <td class="px-3 py-2"
                      :class="selectedBox !== 'All Boxes' ? 'sticky left-0 z-10 bg-white border-r border-gray-200' : ''">
                      Qty</td>
                    <td v-for="main in report.mainSizes" :key="'cell-' + main + '-0'"
                      class="px-3 py-2"
                      :class="{ 'cursor-default': selectedBox === 'All Boxes' && hasCellTooltipLinks(selectedColor, main, 'Qty') }"
                      @mouseenter="showBoxTooltip($event, selectedColor, main, 'Qty')"
                      @mouseleave="scheduleHideBoxTooltip">
                      <span v-if="selectedBox === 'All Boxes'">{{ getQuantity(main, 'Qty') ? getQuantity(main, 'Qty') :
                        ''
                        }}</span>
                      <input v-if="selectedBox !== 'All Boxes'" v-model.number="editedQuantities[main]['Qty']"
                        type="number"
                        class="border-none outline-none rounded-md shadow-none px-3 py-2 w-full min-w-[80px]"
                        @input="handleInput(main, 'Qty')" />
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="mt-6 flex items-center">
        <PrimaryButton @click="printSectionContent">
          Print
        </PrimaryButton>
        <PrimaryButton class="ml-4" @click="openInventoryEmailModal">
          Send Email
        </PrimaryButton>
        <PrimaryButton v-if="report.colorOptions.length > 0" @click="openAddInventoryModal" class="mx-4">
          Add New Inventory
        </PrimaryButton>
        <PrimaryButton v-if="selectedBox !== 'All Boxes'" @click="updateInventory" class="ml-auto">
          Update Inventory
        </PrimaryButton>
      </div>


      <h3 class="font-semibold text-xl text-gray-800 leading-tight py-6" v-if="selectedBox !== 'All Boxes'">
        Edit box
      </h3>

      <h4 class="font-semibold text-l text-gray-800 leading-tight py-6" v-if="selectedBox !== 'All Boxes'">Update Box
        Information</h4>

      <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6"
        v-if="selectedBox !== 'All Boxes' && selectedLocationType === 'warehouse'">
        <div>
          <label for="row" class="block text-sm font-medium text-gray-700 mb-1">Row</label>
          <input id="row" type="text" v-model="row"
            class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
        </div>

        <div>
          <label for="rack" class="block text-sm font-medium text-gray-700 mb-1">Rack</label>
          <input id="rack" type="text" v-model="rack"
            class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
        </div>

        <div>
          <label for="shelf" class="block text-sm font-medium text-gray-700 mb-1">Shelf</label>
          <input id="shelf" type="text" v-model="shelf"
            class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
        </div>

        <div class="flex items-end">
          <PrimaryButton type="button"
            class="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition" @click="updateBoxInfo">
            Update Box Information
          </PrimaryButton>
        </div>
      </div>

      <div class="col-span-4 grid grid-cols-3 sm:grid-cols-3 gap-4 mt-2"
        v-if="selectedBox !== 'All Boxes' && (availableBoxes.filter(b => b !== 'All Boxes' && b !== selectedBox)).length > 0">
        <div>
          <label for="merge-box" class="block text-sm font-medium text-gray-700 mb-1">
            Merge with box:
          </label>
          <SelectInput id="merge-box" v-model="mergeTargetBox" class="mt-1">
            <option disabled value="">Select a box</option>
            <option v-for="box in availableBoxes.filter(b => b !== 'All Boxes' && b !== selectedBox)" :key="box"
              :value="box">
              {{ box }}
            </option>
          </SelectInput>
        </div>

        <div class="flex items-end">
          <PrimaryButton class="text-white px-4 py-2 rounded shadow hover:bg-green-700 transition" @click="mergeBoxes"
            :disabled="!mergeTargetBox">
            Merge Boxes
          </PrimaryButton>
        </div>
      </div>

      <h4 class="font-semibold text-l text-gray-800 leading-tight py-6" v-if="selectedBox !== 'All Boxes'">Update Box
        Location</h4>

      <div class="col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-4" v-if="selectedBox !== 'All Boxes'">
        <div>
          <label for="merge-box" class="block text-sm font-medium text-gray-700 mb-1">
            Select new Location
          </label>
          <SelectInput id="merge-box" v-model="selectedLocation" class="mt-1">
            <option value="">Select a Location</option>
            <option v-for="loc in report.locations" :key="loc.locationId" :value="loc.locationId">
              {{ loc.name }}
            </option>
          </SelectInput>
        </div>

        <div v-if="selectedLocation">
          <label for="storage" class="block text-sm font-medium text-gray-700 mb-1">Select Storage</label>

          <div v-if="report.storages.filter(storage => storage.locationId === selectedLocation).length === 0"
            class="text-red-500 text-sm py-2">
            No storages found for this location.
          </div>

          <SelectInput id="storage" v-model="selectedStorage" class="mt-1">
            <option value="">Select a Storage</option>
            <option v-for="s in report.storages.filter(storage => storage.locationId === selectedLocation)" :key="s.id"
              :value="s.id">
              {{ s.name }}
            </option>
          </SelectInput>

        </div>

        <div v-if="selectedLocation && selectedLocationType === 'conveyor'">
          <label for="location-conveyor-slot" class="block text-sm font-medium text-gray-700 mb-1">
            Conveyor Slot
          </label>
          <input id="location-conveyor-slot" type="number" min="1" v-model.number="locationConveyorSlot"
            class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
        </div>

        <div class="flex items-end" v-if="selectedLocation">
          <PrimaryButton class="text-white px-4 py-2 rounded shadow hover:bg-green-700 transition"
            @click="updateLocation" :disabled="!selectedLocation || !selectedStorage">
            Update Box Location
          </PrimaryButton>
        </div>
      </div>

      <h4 class="font-semibold text-l text-gray-800 leading-tight py-6" v-if="selectedBox !== 'All Boxes'">Delete box
      </h4>

      <PrimaryButton type="button" v-if="selectedBox !== 'All Boxes'"
        class="bg-red-600 text-white py-2 rounded shadow hover:bg-red-700 transition" @click="confirmDeleteBox">
        Delete Box
      </PrimaryButton>
    </div>
  </AppLayout>

  <DialogModal :show="showDeleteModal" @close="cancelDeleteBox">
    <template #title>
      Delete Box
    </template>

    <template #content>
      Are you sure you want to delete the box <strong>{{ selectedBox }}</strong>?
      This action cannot be undone.
    </template>

    <template #footer>
      <SecondaryButton @click="cancelDeleteBox">
        Cancel
      </SecondaryButton>

      <PrimaryButton class="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700 transition ml-2"
        @click="proceedToDeleteBox">
        Delete
      </PrimaryButton>
    </template>
  </DialogModal>

  <DialogModal :show="showSuccessModal" @close="showSuccessModal = false">
    <template #title>
      Box Deleted
    </template>

    <template #content>
      The box was deleted successfully.
    </template>

    <template #footer>
      <PrimaryButton @click="showSuccessModal = false">
        Close
      </PrimaryButton>
    </template>
  </DialogModal>

  <DialogModal :show="sendingInventoryEmail.show" @close="closeInventoryEmailModal">
    <template #title>
      Send Inventory Report Email
    </template>

    <template #content>
      <p v-if="sendingInventoryEmail.error" class="text-red-600">{{ sendingInventoryEmail.error }}</p>

      <div class="mt-2">
        <label for="inv-email" class="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
        <input id="inv-email" type="email" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
          :disabled="sendingInventoryEmail.sending" v-model="sendInventoryEmailForm.email" />
      </div>

      <div class="mt-2">
        <label for="inv-subject" class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
        <input id="inv-subject" type="text" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
          :disabled="sendingInventoryEmail.sending" v-model="sendInventoryEmailForm.subject" />
      </div>

      <div class="mt-2">
        <label for="inv-message" class="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
        <textarea id="inv-message" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-3 py-2" rows="5"
          :disabled="sendingInventoryEmail.sending" v-model="sendInventoryEmailForm.message"
          placeholder="Write a short note that will appear above the inventory table..."></textarea>
      </div>

      <p class="mt-3 text-xs text-gray-500">
        The email body will include this message (if any) and the current report view.
      </p>
    </template>

    <template #footer>
      <SecondaryButton @click="closeInventoryEmailModal" :disabled="sendingInventoryEmail.sending" class="mr-2">
        Cancel
      </SecondaryButton>
      <PrimaryButton @click="sendInventoryEmail" :disabled="sendingInventoryEmail.sending">
        Send
      </PrimaryButton>
    </template>
  </DialogModal>

  <AddInventoryModal :show="showAddInventoryModal" @close="showAddInventoryModal = false" :style-id="report.styleId"
    :style-number="report.style" :garment-type="report.garmentType" :gender="report.gender" :client="report.client"
    :color-options="report.colorOptions" :storages="report.storages" :main-sizes="report.mainSizes"
    :opt-sizes="report.optSizes" />

  <Teleport to="body">
    <div
      v-if="boxTooltip.visible"
      class="fixed z-[9999] max-h-[50vh] overflow-y-auto rounded bg-black p-2 text-xs text-white shadow-lg"
      :style="{ top: boxTooltip.top + 'px', left: boxTooltip.left + 'px' }"
      @mouseenter="keepBoxTooltip"
      @mouseleave="scheduleHideBoxTooltip"
    >
      <ul>
        <li v-for="(link, idx) in boxTooltip.links" :key="idx" class="py-1 whitespace-nowrap">
          <a
            href="javascript:void(0)"
            @click.prevent="selectedBox = link.box; hideBoxTooltipNow()"
            class="underline hover:text-yellow-400"
          >
            {{ link.box }}
          </a>
        </li>
      </ul>
    </div>
  </Teleport>

</template>

<style>
@media print {
  @page {
    size: A4 landscape;
    margin: 0.5cm;
  }

  body {
    margin: 0;
    font-size: 12px;
  }

  .print-area {
    transform: scale(0.95);
    transform-origin: top left;
    width: 100%;
  }

  table {
    width: 100%;
    table-layout: auto;
    font-size: 8px;
  }

  th,
  td {
    padding: 1px;
    font-size: 8px;
  }

  .no-print {
    display: none !important;
  }
}
</style>
